import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react";
import {
  ClerkProvider,
  useAuth as useClerkAuth,
  useClerk as useClerkClient,
} from "@clerk/react";
import { isExplicitE2ETestMode } from "../utils/e2eMode";
import {
  getLocalIdentityProfile,
  isLocalIdentityMode,
} from "../utils/localIdentityMode";
import {
  AUTH_SESSION_INVALIDATED_EVENT,
  AUTH_SESSION_RESTORED_EVENT,
  endAdapterSession,
  hasAdapterSessionEnded,
} from "../services/authSession";
import { registerBrowserAuthTokenProvider } from "../services/authToken";

import { useBrowserAuthSession } from "../hooks/useBrowserAuthSession";
import type { BrowserAuthSession } from "../services/browserAuthSession";

interface AuthContextValue {
  session: BrowserAuthSession;
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  orgId: string | null;
  getToken: () => Promise<string | null>;
  signOut: (options?: { redirectUrl?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  clerkKey?: string;
}

export function AuthProvider({ children, clerkKey }: AuthProviderProps) {
  const e2eTestMode = isExplicitE2ETestMode();
  const localIdentityMode = isLocalIdentityMode();
  const localProfile = localIdentityMode ? getLocalIdentityProfile() : null;
  const adapterMode = e2eTestMode || localIdentityMode;
  const [isAdapterSignedIn, setIsAdapterSignedIn] = useState(
    () => !adapterMode || !hasAdapterSessionEnded(),
  );

  useEffect(() => {
    if (!adapterMode) return undefined;

    const invalidateSession = () => setIsAdapterSignedIn(false);
    const restoreSession = () => setIsAdapterSignedIn(true);
    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, invalidateSession);
    window.addEventListener(AUTH_SESSION_RESTORED_EVENT, restoreSession);
    return () => {
      window.removeEventListener(
        AUTH_SESSION_INVALIDATED_EVENT,
        invalidateSession,
      );
      window.removeEventListener(AUTH_SESSION_RESTORED_EVENT, restoreSession);
    };
  }, [adapterMode]);

  const signOutAdapter = async () => {
    endAdapterSession();
    setIsAdapterSignedIn(false);
  };

  if (adapterMode) {
    const mockE2EValue: Omit<AuthContextValue, "session"> = {
      isLoaded: true,
      isSignedIn: isAdapterSignedIn,
      userId: isAdapterSignedIn
        ? localIdentityMode
          ? (localProfile?.clerkId ?? null)
          : "e2e-user"
        : null,
      orgId: isAdapterSignedIn
        ? localIdentityMode
          ? (localProfile?.tenantId ?? null)
          : "e2e-org"
        : null,
      getToken: async () =>
        isAdapterSignedIn
          ? localIdentityMode
            ? import.meta.env.VITE_LOCAL_IDENTITY_TOKEN
            : "mock-e2e-token"
          : null,
      signOut: signOutAdapter,
    };
    return (
      <SessionContext value={mockE2EValue} providerSession="local-adapter">
        {children}
      </SessionContext>
    );
  }

  if (!clerkKey) {
    const unconfiguredValue: Omit<AuthContextValue, "session"> = {
      isLoaded: true,
      isSignedIn: false,
      userId: null,
      orgId: null,
      getToken: async () => null,
      signOut: async () => undefined,
    };
    return (
      <SessionContext value={unconfiguredValue} providerSession="unconfigured">
        {children}
      </SessionContext>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkKey}>
      <ClerkAuthWrapper>{children}</ClerkAuthWrapper>
    </ClerkProvider>
  );
}

function ClerkAuthWrapper({ children }: { children: ReactNode }) {
  const clerkAuth = useClerkAuth();
  const clerkClient = useClerkClient();

  const getClerkToken = useCallback(async () => {
    try {
      return await clerkAuth.getToken();
    } catch {
      return null;
    }
  }, [clerkAuth.getToken]);

  useEffect(() => {
    const invalidateSession = () => {
      void clerkClient.signOut({ redirectUrl: "/sign-in" }).catch(() => {
        window.location.replace("/sign-in");
      });
    };
    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, invalidateSession);
    return () =>
      window.removeEventListener(
        AUTH_SESSION_INVALIDATED_EVENT,
        invalidateSession,
      );
  }, [clerkClient]);

  const authValue: Omit<AuthContextValue, "session"> = {
    isLoaded: clerkAuth.isLoaded,
    isSignedIn: clerkAuth.isSignedIn ?? false,
    userId: clerkAuth.userId ?? null,
    orgId: clerkAuth.orgId ?? null,
    getToken: getClerkToken,
    signOut: async (options) => clerkClient.signOut(options),
  };

  return (
    <SessionContext
      value={authValue}
      providerSession={clerkAuth.sessionId ?? null}
    >
      {children}
    </SessionContext>
  );
}

function SessionContext({
  value,
  providerSession,
  children,
}: {
  value: Omit<AuthContextValue, "session">;
  providerSession: string | null;
  children: ReactNode;
}) {
  const sourceIdentity = JSON.stringify([
    providerSession,
    value.userId,
    value.orgId,
    value.isSignedIn,
    value.isLoaded,
  ]);
  const [logoutFor, setLogoutFor] = useState<string | null>(null);
  useLayoutEffect(() => {
    setLogoutFor(null);
  }, [sourceIdentity]);
  const signedIn = value.isSignedIn && logoutFor !== sourceIdentity;
  const identity = JSON.stringify([sourceIdentity, signedIn]);
  const { session, invalidate } = useBrowserAuthSession(identity, () =>
    signedIn ? value.getToken() : Promise.resolve(null),
  );
  useLayoutEffect(
    () => registerBrowserAuthTokenProvider(session.getToken),
    [session],
  );
  useLayoutEffect(() => {
    const endCapturedSession = () => {
      invalidate();
      setLogoutFor(sourceIdentity);
    };
    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, endCapturedSession);
    return () =>
      window.removeEventListener(
        AUTH_SESSION_INVALIDATED_EVENT,
        endCapturedSession,
      );
  }, [invalidate, sourceIdentity]);
  const signOut: AuthContextValue["signOut"] = async (options) => {
    if (!session.isCurrent()) return;
    invalidate();
    setLogoutFor(sourceIdentity);
    try {
      await value.signOut(options);
    } catch (error) {
      setLogoutFor((previous) =>
        previous === sourceIdentity ? null : previous,
      );
      throw error;
    }
  };
  return (
    <AuthContext.Provider
      value={{
        ...value,
        isSignedIn: signedIn,
        getToken: session.getToken,
        session,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return auth;
}
